import { supabase } from '@/core/database/supabase';
import type { ProjectZone, ZoneStats, ZoneFilters } from '../types/zones.types';

export const ZonesService = {
  /**
   * Lấy danh sách vùng với bộ lọc
   */
  async getZones(companyId: string, filters: ZoneFilters = {}): Promise<{ data: ProjectZone[], total: number }> {
    let query = supabase
      .from('project_zones')
      .select(`
        *,
        project:projects(id, name, code),
        contract:maintenance_contracts(id, contract_name, contract_code),
        manager:user_profiles!manager_user_id(id, full_name, avatar_url:avatar_path)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    // Apply Filters
    if (filters.search) {
      query = query.or(`zone_name.ilike.%${filters.search}%,zone_code.ilike.%${filters.search}%`);
    }
    if (filters.status && filters.status !== 'all') {
      query = query.eq('status_code', filters.status);
    }
    if (filters.type && filters.type !== 'all') {
      query = query.eq('zone_type', filters.type);
    }
    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }
    if (filters.contractId) {
      query = query.eq('contract_id', filters.contractId);
    }

    // Pagination
    const page = filters.page || 1;
    const pageSize = filters.pageSize || 10;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    query = query
      .order('created_at', { ascending: false })
      .range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ProjectZone[],
      total: count || 0
    };
  },

  /**
   * Lấy chi tiết vùng
   */
  async getZoneDetail(id: string): Promise<ProjectZone | null> {
    const { data, error } = await supabase
      .from('project_zones')
      .select(`
        *,
        project:projects(id, name, code),
        contract:maintenance_contracts(id, contract_name, contract_code),
        parent_zone:project_zones!parent_zone_id(id, zone_name, zone_code),
        manager:user_profiles!manager_user_id(id, full_name, avatar_url:avatar_path)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data as ProjectZone;
  },

  /**
   * Tạo vùng mới
   */
  async createZone(data: Partial<ProjectZone>): Promise<ProjectZone> {
    const { data: result, error } = await supabase
      .from('project_zones')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ProjectZone;
  },

  /**
   * Cập nhật vùng
   */
  async updateZone(id: string, updates: Partial<ProjectZone>): Promise<ProjectZone> {
    const { data, error } = await supabase
      .from('project_zones')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as ProjectZone;
  },

  /**
   * Xóa mềm vùng
   */
  async deleteZone(id: string): Promise<void> {
    const { error } = await supabase
      .from('project_zones')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Lấy thống kê vùng
   */
  async getZoneStats(companyId: string): Promise<ZoneStats> {
    const { data, error } = await supabase
      .from('project_zones')
      .select('status_code, zone_type, area_m2')
      .eq('company_id', companyId);

    if (error) throw error;

    const stats: ZoneStats = {
      total: data.length,
      active: data.filter(z => z.status_code === 'active').length,
      totalArea: data.reduce((sum, z) => sum + (z.area_m2 || 0), 0),
      byType: data.reduce((acc, z) => {
        const type = z.zone_type as any;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as any),
    };

    return stats;
  }
};
